import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { BibleBook, ReadingPlanDetail } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { PassagePicker } from './PassagePicker';

type DraftDay = { title: string; passages: string; reflection: string };
const emptyDay = (): DraftDay => ({ title: '', passages: '', reflection: '' });

/** Una cita por línea (o separadas por punto y coma). Bíblico las valida en el servidor. */
export const splitPassages = (text: string) => text.split(/[;\n]/).map((s) => s.trim()).filter(Boolean);

/**
 * Llenar días: los capítulos de los libros elegidos (del primero al último, en orden de la Biblia),
 * repartidos de `perDay` en `perDay`. Un capítulo por línea; a lo más 10 por día (lo que acepta el servidor).
 */
export function fillDays(books: BibleBook[], fromCode: string, toCode: string, perDay: number): DraftDay[] {
  const a = books.findIndex((b) => b.code === fromCode);
  const b = books.findIndex((x) => x.code === toCode);
  if (a < 0 || b < 0) return [];
  const [start, end] = a <= b ? [a, b] : [b, a];
  const chapters = books.slice(start, end + 1).flatMap((book) => Array.from({ length: book.chapters }, (_, i) => `${book.name} ${i + 1}`));
  const n = Math.min(Math.max(Math.round(perDay) || 1, 1), 10);
  const out: DraftDay[] = [];
  for (let i = 0; i < chapters.length; i += n) out.push({ title: '', passages: chapters.slice(i, i + n).join('\n'), reflection: '' });
  return out;
}

/** Crear o editar un plan de lectura. Publicado, se corrige sin borrar el avance de quien lo sigue. */
export function PlanBuilderPage() {
  const { planId } = useParams();
  const me = useMe();
  const navigate = useNavigate();
  const churchId = me.activeChurchId;
  const [loaded, setLoaded] = useState(!planId);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [days, setDays] = useState<DraftDay[]>([emptyDay()]);
  const [original, setOriginal] = useState<{ status: ReadingPlanDetail['status']; totalDays: number; enrolled: number | null } | null>(null);
  const [filling, setFilling] = useState(false);
  const books = useAsync((signal) => api.get<{ items: BibleBook[] }>('/bible/books', signal).then((r) => r.items), []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!planId || !churchId) return;
    const controller = new AbortController();
    api
      .get<ReadingPlanDetail>(`/devotional/churches/${churchId}/plans/${planId}`, controller.signal)
      .then((p) => {
        setTitle(p.title);
        setDescription(p.description ?? '');
        setDays(p.days.map((d) => ({ title: d.title ?? '', passages: d.passages.join('\n'), reflection: d.reflection ?? '' })));
        setOriginal({ status: p.status, totalDays: p.totalDays, enrolled: p.enrolled });
        setLoaded(true);
      })
      .catch((e: unknown) => controller.signal.aborted || setError(errorMessage(e)));
    return () => controller.abort();
  }, [churchId, planId]);

  const published = original?.status === 'published';
  const update = (i: number, change: Partial<DraftDay>) => setDays((ds) => ds.map((d, j) => (j === i ? { ...d, ...change } : d)));

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const missing = days.findIndex((d) => splitPassages(d.passages).length === 0);
    if (missing >= 0) return setError(`Escribe al menos una cita en el día ${missing + 1}.`);
    if (published && days.length !== original!.totalDays) {
      const ok = window.confirm(
        `El plan tenía ${original!.totalDays} días y ahora tendrá ${days.length}. Quienes ya lo siguen conservan los días que marcaron como leídos${
          days.length < original!.totalDays ? '; no se pueden quitar días que alguien ya leyó' : ''
        }. ¿Guardar los cambios?`,
      );
      if (!ok) return;
    }
    setBusy(true);
    setError(null);
    const body = {
      title,
      description: description.trim() || null,
      days: days.map((d) => ({ title: d.title.trim() || null, passages: splitPassages(d.passages), reflection: d.reflection.trim() || null })),
    };
    try {
      const saved = planId
        ? await api.put<ReadingPlanDetail>(`/devotional/churches/${churchId}/plans/${planId}`, body)
        : await api.post<ReadingPlanDetail>(`/devotional/churches/${churchId}/plans`, body);
      navigate(`/app/devocional/${saved.id}`, { replace: true });
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;

  return (
    <div className="page page--narrow">
      <p className="small">
        <Link to="/app/devocional?vista=administrar">← Planes de lectura</Link>
      </p>
      <header className="page__header">
        <h1>{planId ? 'Editar plan' : 'Nuevo plan'}</h1>
        {published ? (
          <p className="muted">
            Este plan ya está publicado. Puedes corregir citas y textos y agregar días al final: quienes lo siguen conservan su avance. Solo se pueden quitar días del final que nadie haya marcado como leídos.
          </p>
        ) : (
          <p className="muted">Se guarda como borrador hasta que lo publiques. Después podrás corregir citas y textos sin borrar el avance de quien lo siga.</p>
        )}
      </header>
      {!loaded && !error ? <Loading /> : null}
      {loaded ? (
        <form className="form" onSubmit={submit} noValidate>
          <Card>
            <Field label="Nombre del plan" value={title} onChange={(e) => setTitle(e.target.value)} required minLength={3} maxLength={120} />
            <div className="field">
              <label htmlFor="plan-description">Descripción (opcional)</label>
              <textarea id="plan-description" rows={2} maxLength={1000} value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
          </Card>
          {filling && books.status === 'ready' ? (
            <FillDaysCard
              books={books.data}
              onCancel={() => setFilling(false)}
              onFill={(generated) => {
                // Los días vacíos del final se reemplazan; los que ya tienen citas se quedan.
                setDays((ds) => {
                  const kept = [...ds];
                  while (kept.length && !kept.at(-1)!.passages.trim() && !kept.at(-1)!.title.trim() && !kept.at(-1)!.reflection.trim()) kept.pop();
                  return [...kept, ...generated].slice(0, 366);
                });
                setFilling(false);
              }}
            />
          ) : (
            <div className="actions">
              <Button type="button" variant="secondary" onClick={() => setFilling(true)} disabled={books.status !== 'ready'}>
                Llenar días automáticamente
              </Button>
            </div>
          )}
          {days.map((d, i) => (
            <Card
              key={i}
              title={`Día ${i + 1}`}
              action={
                days.length > 1 ? (
                  <Button type="button" variant="ghost" onClick={() => setDays((ds) => ds.filter((_, j) => j !== i))} aria-label={`Quitar el día ${i + 1}`}>
                    Quitar
                  </Button>
                ) : null
              }
            >
              <Field label={`Título del día ${i + 1} (opcional)`} value={d.title} onChange={(e) => update(i, { title: e.target.value })} maxLength={120} />
              <div className="field">
                <label htmlFor={`passages-${i}`}>Citas del día {i + 1}</label>
                <textarea id={`passages-${i}`} rows={2} value={d.passages} onChange={(e) => update(i, { passages: e.target.value })} placeholder={'Juan 3:1-21\nSalmos 23'} aria-describedby={`passages-${i}-hint`} />
                <p id={`passages-${i}-hint`} className="field__hint">
                  Una cita por línea.
                </p>
              </div>
              <PassagePicker
                label={`Buscar una cita para el día ${i + 1}`}
                books={books.status === 'ready' ? books.data : undefined}
                onPick={(ref) => update(i, { passages: d.passages.trim() ? `${d.passages.trimEnd()}\n${ref}` : ref })}
              />
              <div className="field">
                <label htmlFor={`reflection-${i}`}>Reflexión o pregunta (opcional)</label>
                <textarea id={`reflection-${i}`} rows={2} maxLength={3000} value={d.reflection} onChange={(e) => update(i, { reflection: e.target.value })} />
              </div>
            </Card>
          ))}
          <div className="actions actions--wrap">
            <Button type="button" variant="secondary" onClick={() => setDays((ds) => [...ds, emptyDay()])} disabled={days.length >= 366}>
              Agregar día
            </Button>
          </div>
          {error ? <Alert>{error}</Alert> : null}
          <Button type="submit" busy={busy}>
            {published ? 'Guardar cambios' : 'Guardar borrador'}
          </Button>
        </form>
      ) : null}
      {error && !loaded ? <Alert>{error}</Alert> : null}
    </div>
  );
}

/** «Llenar días automáticamente»: de qué libro a qué libro y cuántos capítulos por día. */
function FillDaysCard({ books, onFill, onCancel }: { books: BibleBook[]; onFill: (days: DraftDay[]) => void; onCancel: () => void }) {
  const [from, setFrom] = useState(books[0]?.code ?? '');
  const [to, setTo] = useState(books[0]?.code ?? '');
  const [perDay, setPerDay] = useState(1);
  const preview = fillDays(books, from, to, perDay);
  return (
    <Card title="Llenar días automáticamente">
      <div className="form__row">
        <div className="field">
          <label htmlFor="fill-from">Desde el libro</label>
          <select id="fill-from" value={from} onChange={(e) => setFrom(e.target.value)}>
            {books.map((b) => <option key={b.code} value={b.code}>{b.name}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor="fill-to">Hasta el libro</label>
          <select id="fill-to" value={to} onChange={(e) => setTo(e.target.value)}>
            {books.map((b) => <option key={b.code} value={b.code}>{b.name}</option>)}
          </select>
        </div>
      </div>
      <Field label="Capítulos por día" type="number" min={1} max={10} value={perDay} onChange={(e) => setPerDay(Number(e.target.value))} />
      <p className="small muted">
        {preview.length} {preview.length === 1 ? 'día' : 'días'}. Para un solo libro, elige el mismo en «Desde» y «Hasta». Los días se agregan al final del plan.
      </p>
      <div className="actions">
        <Button type="button" onClick={() => onFill(preview)} disabled={preview.length === 0}>
          Crear {preview.length} {preview.length === 1 ? 'día' : 'días'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </Card>
  );
}
